/*
 * Mutation queue — write-while-offline bridge.
 *
 * When the network is unavailable, mutations (POST / PATCH / DELETE) that
 * failed with a network error are saved to IndexedDB and replayed in order
 * once the device reconnects.
 *
 * Usage (in React Query mutation fns):
 *
 *   import { sendOrQueue } from '../lib/mutationQueue'
 *
 *   mutationFn: (body) => sendOrQueue('POST', `/trips/${id}/points`, body, id)
 *
 * The hook `useSyncQueue` drains the queue automatically on reconnect.
 */
import { apiSend } from '../api/httpClient'
import {
  enqueueMutation,
  peekMutations,
  deleteMutation,
  type MutationRecord,
} from './offlineStore'

export type { MutationRecord }

/** True when the browser reports no network connectivity. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

/**
 * Attempt the API call immediately; if the network is down (navigator.onLine
 * === false or fetch throws a TypeError/NetworkError), save the mutation to
 * IndexedDB and return `null` so optimistic UI can continue.
 */
export async function sendOrQueue<T = unknown>(
  method: 'POST' | 'PATCH' | 'DELETE' | 'PUT',
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any,
  tripId: string,
  optimisticId?: string,
): Promise<T | null> {
  if (!isOffline()) {
    try {
      return await apiSend<T>(method, url, body)
    } catch (err) {
      if (!isNetworkError(err)) throw err
      // Fall through to queue
    }
  }

  const queuedId = await enqueueMutation({ tripId, method, url, body, optimisticId })
  if (queuedId === null) throw new Error('Offline storage is unavailable or full; the change was not saved.')
  return null
}

/**
 * Drain the queue for a trip — replay all pending mutations in FIFO order.
 * Successfully replayed mutations are deleted. Every failure preserves the
 * record for inspection and retry, including server errors and authorization.
 *
 * Returns the number of successfully replayed mutations.
 */
export async function drainQueue(tripId: string): Promise<number> {
  const pending = await peekMutations(tripId)
  let replayed = 0

  for (const mut of pending) {
    try {
      await apiSend(mut.method, mut.url, mut.body ?? undefined)
      await deleteMutation(mut.id!)
      replayed++
    } catch (err) {
      break
    }
  }

  return replayed
}

function isNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return (
    err.name === 'TypeError' ||
    err.message.toLowerCase().includes('network') ||
    err.message.toLowerCase().includes('failed to fetch')
  )
}
